import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, CreditCard, Mail } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for subscription state
const SubscriptionContext = createContext(null);

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Fetch subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscription = () => {
    fetchSubscription();
  };

  return (
    <SubscriptionContext.Provider value={{ subscription, loading, refreshSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Guard component - always renders children (layout stays visible), shows modal overlay when expired
export const SubscriptionGuard = ({ children }) => {
  const { user } = useAuth();
  const { subscription, loading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if subscription is expired (free plan never expires)
  // Skip subscription check for impersonated sessions
  const isImpersonating = !!localStorage.getItem('inspirotracker_impersonation');
  const isExpired = !isImpersonating && !loading && subscription?.has_subscription && !subscription?.is_valid && subscription?.plan !== 'free' && !subscription?.is_internal;
  const isAdmin = user?.role === 'admin';
  const isExemptPage = location.pathname === '/billing' || location.pathname === '/admin/people';

  // Always render children so sidebar/header remain visible
  return (
    <>
      {children}

      {/* Non-dismissible modal overlay when subscription is expired (except on billing/people pages) */}
      {isExpired && !isExemptPage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
          data-testid="subscription-expired-overlay"
        >
          <div className="max-w-md w-full mx-4 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" data-testid="expired-title">
              {subscription?.plan === 'trial' ? 'Trial Expired' : 'Subscription Expired'}
            </h1>

            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Your {subscription?.plan === 'trial' ? 'free trial' : 'subscription'} has ended.
              {isAdmin
                ? ' Subscribe now to continue using Inspiro Tracker.'
                : ' Please contact your administrator to renew the subscription.'}
            </p>

            {isAdmin ? (
              <>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mb-4">
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    ₹{subscription?.price_per_user || 50}
                    <span className="text-base font-normal text-slate-500">/user/month</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {subscription?.user_count || 1} user(s) = ₹{subscription?.monthly_cost || 50}/month
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-500/20 rounded-xl p-3 mb-5 text-left">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Want to reduce your team size first? Go to <button onClick={() => navigate('/admin/people')} className="font-semibold underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100">People</button> to deactivate users before subscribing.
                  </p>
                </div>

                <Button
                  onClick={() => navigate('/billing')}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  data-testid="subscribe-now-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Subscribe Now
                </Button>
              </>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-500/20 rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 text-blue-700 dark:text-blue-300">
                  <Mail className="w-5 h-5" />
                  <span>Contact your admin to continue</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionGuard;
